import {
    IPatient,
    IVaccination,
    JobType,
    v2,
    constants,
    DispositionType,
    parseDuration,
    dateOrNull,
    parseDateTime,
    IRoute,
    Job,
    IOnDemand,
} from '../../..';
import { personJobAdapter } from './personJob.adapter';
import { contactReferenceAdapter } from './contactReference.adapter';
import { vaccinationAdapter } from './vaccination.adapter';
import { getDefaults } from './helpers/getDefaults';
import { patientAdapter } from './patient.adapter';
import { noteAdapter } from './note.adapter';

export const jobAdapter = (patientV1: IPatient): v2.Job => {
    const {
        id,
        disposition,
        startDateTime,
        dateOfVisit,
        organisation,
        careComplexity,
        activityType,
        duration,
        availableFrom,
        availableTo,
        earliestDateOfVisit,
        carRequired,
        initialVisit,
        version,
        latitude,
        longitude,
        lastUpdatedDateTime,
        createdBy,
        lastUpdatedBy,
        nominatedHcps,
        hcpsNotified,
        expiryDateTime,
    } = patientV1;

    if (!id) {
        throw new Error('No `id` present in data supplied to adapter');
    }

    let { jobType } = patientV1;

    if (jobType === JobType.UCR) {
        jobType = patientV1.disposition === DispositionType.ADMIN ? 'administrative' : 'visit';
    } else if (Job.isOnDemand(jobType)) {
        jobType = 'onDemand';
    }

    const personJobs: v2.PersonJob[] = personJobAdapter(patientV1);

    const plannedDate = dateOfVisit || startDateTime;

    const parsedDuration = parseDuration(duration);
    const plannedDuration = parsedDuration ? parsedDuration.as('minutes') : null;

    const defaults = getDefaults(patientV1);

    const result: v2.Job = {
        ...defaults,
        id,
        personJobs,
        jobType: {
            ...defaults,
            id: jobType,
            name: jobType,
        },
        jobTypeId: jobType,
        patientId: constants.NOT_CAPTURED,
        updatedAt: lastUpdatedDateTime ? new Date(lastUpdatedDateTime) : null,
        createdById: createdBy || constants.NOT_CAPTURED,
        updatedById: lastUpdatedBy || null,
        unitId: organisation || constants.NOT_CAPTURED,
        unit: {
            ...defaults,
            id: organisation || constants.NOT_CAPTURED,
            name: organisation || constants.NOT_CAPTURED,
            isDeleted: false,
        },
        isTemplate: false,
        version,
        latitude: latitude || null,
        longitude: longitude || null,
        plannedDate: plannedDate ? new Date(plannedDate) : null,
        plannedTime: startDateTime ? new Date(startDateTime) : null,
        plannedDuration,
        carRequired: typeof carRequired === 'boolean' ? carRequired : null,
        notes: noteAdapter(patientV1),
        availableFrom: parseDateTime(availableFrom)?.toJSDate() || dateOrNull(availableFrom),
        availableTo: parseDateTime(availableTo)?.toJSDate() || dateOrNull(availableTo),
        patient: patientAdapter(patientV1),
        contactReferences: contactReferenceAdapter(patientV1),
        warnings: [],
        originalJobInSeriesId: initialVisit || null,
        personJobAllowedPersonIds: nominatedHcps || null,
        personJobNotifiedPersonIds: hcpsNotified || null,
        expiryDateTime: expiryDateTime ? new Date(expiryDateTime) : null,
    };

    if (patientV1.jobType === JobType.VACCINATION) {
        result.vaccination = vaccinationAdapter(patientV1 as IVaccination);
    }

    if (patientV1.jobType === JobType.ROUTE) {
        const { itineraryId, routeType } = (patientV1 as unknown) as IRoute;

        result.route = {
            ...defaults,
            id: constants.NOT_CAPTURED,
            jobId: constants.NOT_CAPTURED,
            itineraryId,
            routeType,
        };
    }

    if (Job.isOnDemand(patientV1.jobType)) {
        const {
            speciality,
            hcpPriceInPence,
            priceInPence,
            hidePriceInApp,
            blockSize,
        } = patientV1 as IOnDemand;

        result.onDemand = {
            ...defaults,
            onDemandType: patientV1.jobType,
            id: constants.NOT_CAPTURED,
            jobId: constants.NOT_CAPTURED,
            specialities: speciality,
            blockSize,
            priceInPence: hcpPriceInPence ?? priceInPence,
            hidePrice: hidePriceInApp,
        };
    }

    if (patientV1.jobType === JobType.UCR) {
        const isAdminJob = patientV1.disposition === DispositionType.ADMIN;

        if (isAdminJob) {
            result.administrative = {
                ...defaults,
                id: constants.NOT_CAPTURED,
                jobId: constants.NOT_CAPTURED,
                name: constants.NOT_CAPTURED,
                activityType: activityType || constants.NOT_CAPTURED,
            };
        } else {
            result.visit = {
                ...defaults,
                id: constants.NOT_CAPTURED,
                jobId: constants.NOT_CAPTURED,
                careComplexity,
                earliestDateTime: earliestDateOfVisit ? new Date(earliestDateOfVisit) : null,
                service: disposition,
            };
        }
    }

    return result;
};
